import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Dialog, DialogContent, DialogTitle } from '../ui/custom/Dialog';
import { apiKeyService, ApiKey, CreateApiKeyPayload } from '../../services/apiKeyService';
import {
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ALLOWED_SCOPES = [
  {
    value: 'ALL',
    label: 'All (Wildcard)',
    description: 'Full access — grants all current and future permissions'
  },
  { value: 'tickets:create', label: 'Create Ticket', description: 'Allows creating new tickets' },
  {
    value: 'tickets:update-status',
    label: 'Update Ticket Status',
    description: 'Allows updating ticket status'
  },
  {
    value: 'customers:create',
    label: 'Create Customer',
    description: 'Allows creating new customers'
  },
  {
    value: 'form-templates:create',
    label: 'Create Form Template',
    description: 'Allows creating new form templates'
  }
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function ScopeBadge({ scope, isDark }: { scope: string; isDark: boolean }) {
  const isWildcard = scope === 'ALL';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        isWildcard
          ? isDark
            ? 'bg-yellow-900/40 text-yellow-300'
            : 'bg-yellow-100 text-yellow-800'
          : isDark
            ? 'bg-indigo-900/40 text-indigo-300'
            : 'bg-indigo-100 text-indigo-700'
      }`}
    >
      {scope}
    </span>
  );
}

export default function ApiKeyManagement() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isNewKeyOpen, setIsNewKeyOpen] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [isRevokeOpen, setIsRevokeOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // Create form state
  const [form, setForm] = useState<CreateApiKeyPayload>({
    name: '',
    description: '',
    scopes: [],
    expiresAt: ''
  });
  const [creating, setCreating] = useState(false);

  const loadApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const keys = await apiKeyService.getTenantApiKeys();
      setApiKeys(keys);
    } catch {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleScopeToggle = (scope: string) => {
    setForm((prev) => {
      const current = prev.scopes ?? [];
      if (current.includes(scope)) {
        return { ...prev, scopes: current.filter((s) => s !== scope) };
      }
      // If wildcard selected, clear others; if other selected while wildcard active, remove wildcard
      if (scope === 'ALL') {
        return { ...prev, scopes: ['ALL'] };
      }
      return { ...prev, scopes: current.filter((s) => s !== 'ALL').concat(scope) };
    });
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.scopes || form.scopes.length === 0) {
      toast.error('Select at least one scope');
      return;
    }

    try {
      setCreating(true);
      const payload: CreateApiKeyPayload = {
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        scopes: form.scopes,
        expiresAt: form.expiresAt || undefined
      };
      const result = await apiKeyService.createApiKey(payload);
      setNewlyCreatedKey(result.apiKey);
      setIsCreateOpen(false);
      setIsNewKeyOpen(true);
      setForm({ name: '', description: '', scopes: [], expiresAt: '' });
      await loadApiKeys();
    } catch {
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newlyCreatedKey);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleNewKeyClose = () => {
    setIsNewKeyOpen(false);
    setNewlyCreatedKey('');
    setCopied(false);
  };

  const confirmRevoke = (key: ApiKey) => {
    setRevokeTarget(key);
    setIsRevokeOpen(true);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setRevoking(true);
      await apiKeyService.revokeApiKey(revokeTarget.id);
      toast.success(`API key "${revokeTarget.name}" revoked`);
      setIsRevokeOpen(false);
      setRevokeTarget(null);
      await loadApiKeys();
    } catch {
      toast.error('Failed to revoke API key');
    } finally {
      setRevoking(false);
    }
  };

  const activeKeys = apiKeys.filter((k) => !k.revoked_at);
  const revokedKeys = apiKeys.filter((k) => k.revoked_at);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            Keys
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage API keys for external integrations. Keys grant access to your tenant's data.
          </p>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New API Key
        </button>
      </div>

      {/* Active Keys */}
      <div
        className={`rounded-lg border ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        } overflow-hidden`}
      >
        {loading ? (
          <div className={`p-8 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading...
          </div>
        ) : activeKeys.length === 0 ? (
          <div className={`p-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <KeyIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No active API keys. Create one to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className={`${isDark ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}
              >
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Name
                </th>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Scopes
                </th>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Last Used
                </th>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Expires
                </th>
                <th
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Created
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
              {activeKeys.map((key) => (
                <tr
                  key={key.id}
                  className={`${isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <td
                    className={`px-4 py-3 font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    <div>{key.name}</div>
                    {key.description && (
                      <div
                        className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        {key.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {key.scopes && key.scopes.length > 0 ? (
                        key.scopes.map((s) => <ScopeBadge key={s} scope={s} isDark={isDark} />)
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          None
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {formatDate(key.last_used_at)}
                  </td>
                  <td className={`px-4 py-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {formatDate(key.expires_at)}
                  </td>
                  <td className={`px-4 py-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {formatDate(key.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => confirmRevoke(key)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        isDark ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Revoked Keys (collapsed) */}
      {revokedKeys.length > 0 && (
        <details className="mt-4">
          <summary
            className={`cursor-pointer text-sm select-none ${
              isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {revokedKeys.length} revoked key{revokedKeys.length !== 1 ? 's' : ''}
          </summary>
          <div
            className={`mt-2 rounded-lg border ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            } overflow-hidden`}
          >
            <table className="w-full text-sm">
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {revokedKeys.map((key) => (
                  <tr key={key.id} className="opacity-60">
                    <td
                      className={`px-4 py-2 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      {key.name}
                    </td>
                    <td
                      className={`px-4 py-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                    >
                      Revoked {formatDate(key.revoked_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Create API Key Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent
          className={`max-w-md ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}
        >
          <DialogTitle className={isDark ? 'text-gray-100' : 'text-gray-900'}>
            Create API Key
          </DialogTitle>

          <div className="mt-4 space-y-4">
            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. CRM Integration"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
              >
                Description
              </label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
              >
                Scopes <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {ALLOWED_SCOPES.map((scope) => {
                  const checked = form.scopes?.includes(scope.value) ?? false;
                  const wildcardActive = form.scopes?.includes('ALL') ?? false;
                  const isDisabled = scope.value !== 'ALL' && wildcardActive;
                  return (
                    <label
                      key={scope.value}
                      className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${
                        isDisabled
                          ? `cursor-not-allowed opacity-40 ${isDark ? 'border-gray-700' : 'border-gray-200'}`
                          : checked
                            ? `cursor-pointer ${isDark ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-500 bg-indigo-50'}`
                            : `cursor-pointer ${isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'}`
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isDisabled}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed"
                      />
                      <div>
                        <div
                          className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}
                        >
                          {scope.label}
                        </div>
                        <div
                          className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                        >
                          {scope.description}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
              >
                Expires At
              </label>
              <input
                type="date"
                value={form.expiresAt}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Leave blank for a non-expiring key
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setIsCreateOpen(false)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {creating ? 'Creating...' : 'Create Key'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Key Reveal Dialog */}
      <Dialog open={isNewKeyOpen} onOpenChange={() => {}}>
        <DialogContent
          className={`max-w-lg ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}
          // Prevent closing by clicking outside — user must explicitly acknowledge
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-full ${isDark ? 'bg-green-900/30' : 'bg-green-100'}`}>
              <ShieldCheckIcon
                className={`h-6 w-6 ${isDark ? 'text-green-400' : 'text-green-600'}`}
              />
            </div>
            <DialogTitle className={isDark ? 'text-gray-100' : 'text-gray-900'}>
              API Key Created
            </DialogTitle>
          </div>

          {/* Security Warning */}
          <div
            className={`flex gap-3 p-3 rounded-md mb-4 ${
              isDark
                ? 'bg-yellow-900/20 border border-yellow-700/50'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <ExclamationTriangleIcon
              className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}
            />
            <div className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
              <p className="font-semibold mb-1">Save this key now — it won't be shown again.</p>
              <p>
                For security, we only store a hash of your key. If you lose it, you'll need to
                revoke this key and create a new one.
              </p>
            </div>
          </div>

          {/* Key Display */}
          <div
            className={`flex items-center gap-2 p-3 rounded-md font-mono text-sm break-all ${
              isDark ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'
            }`}
          >
            <span className={`flex-1 select-all ${isDark ? 'text-green-300' : 'text-gray-800'}`}>
              {newlyCreatedKey}
            </span>
            <button
              onClick={handleCopy}
              title="Copy to clipboard"
              className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                copied
                  ? isDark
                    ? 'text-green-400'
                    : 'text-green-600'
                  : isDark
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              {copied ? (
                <ClipboardDocumentCheckIcon className="h-5 w-5" />
              ) : (
                <ClipboardDocumentIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Include this key in API requests as:{' '}
            <code
              className={`px-1 py-0.5 rounded ${
                isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Authorization: Bearer &lt;key&gt;
            </code>
          </p>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleNewKeyClose}
              className="px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              I've saved my key
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={isRevokeOpen} onOpenChange={setIsRevokeOpen}>
        <DialogContent
          className={`max-w-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-full ${isDark ? 'bg-red-900/30' : 'bg-red-100'}`}>
              <ExclamationTriangleIcon
                className={`h-5 w-5 ${isDark ? 'text-red-400' : 'text-red-600'}`}
              />
            </div>
            <DialogTitle className={isDark ? 'text-gray-100' : 'text-gray-900'}>
              Revoke API Key
            </DialogTitle>
          </div>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Are you sure you want to revoke{' '}
            <span className="font-semibold">{revokeTarget?.name}</span>? Any integrations using this
            key will immediately lose access and cannot be recovered.
          </p>
          <div className="flex justify-end gap-3 mt-5">
            <button
              onClick={() => setIsRevokeOpen(false)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {revoking ? 'Revoking...' : 'Revoke Key'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
